import IDataComponent from 'src/models/IDataComponent'
import { INamedEntity, ITimestampedEntity } from 'src/models/IEntity'
import IJob, { JobStatus, JobType } from 'src/models/IJob'

// a backfill is named `<ingestion>.<id>.backfill`, for the ingestion that it backfills, where its id makes the name unique
export const backfillNameSuffix = '.backfill'

export enum PipelineStatus {
    Draft = 'DRAFT',
    Failed = 'FAILED',
    Provisioning = 'PROVISIONING',
    Ready = 'READY'
}

/**
 * What a pipeline does (MEL-702). An ingestion copies one source into one data store (mappers
 * allowed) and nothing else; a workflow is any other pipeline, e.g. one that runs dbt. Every
 * pipeline that predates the type is a workflow, and a workflow can be converted into an ingestion.
 */
export enum PipelineType {
    Ingestion = 'INGESTION',
    Workflow = 'WORKFLOW'
}

/**
 * Stability of a pipeline over its recent run attempts (MEL-657).
 *
 * Every attempt counts, retries included: an attempt that failed and was retried (`WARNING`) is a
 * failure the verdict counts, because a pipeline that has to be retried is not one that works.
 *
 * `UNKNOWN` is not "healthy" — it means there was no finished attempt to judge (a pipeline that
 * has never run, or whose only runs were cancelled or are still in flight), so it must render no
 * verdict at all rather than reading as stable.
 */
export enum PipelineHealthStatus {
    Stable = 'STABLE',
    Unstable = 'UNSTABLE',
    Unknown = 'UNKNOWN'
}

export interface IPipelineHealth {
    status: PipelineHealthStatus
    /** How many of those attempts failed — retried ones (`WARNING`) among them. */
    failureCount: number
    /**
     * How many attempts the verdict is based on — up to the backend's window of 5, but fewer for a
     * pipeline that has not run 5 times yet. Lets the UI qualify the verdict instead of implying a
     * full window.
     */
    runsConsidered: number
    /**
     * Outcome of each of those attempts, newest first — `COMPLETE`, `ERROR`, or `WARNING` for one
     * that failed and was retried. Drives the run-history dots, which colour those three green,
     * red and amber.
     */
    recentRuns: JobStatus[]
}

interface IPipelineEmbedded {
    dataComponents: IDataComponent[]
    // the latest job of any kind, connection checks included: what is in flight
    'latest job'?: IJob
    // the latest run, passing over the connection checks since it (MEL-702)
    'latest run'?: IJob
    // the latest job, when it is a connection check (Verify, Discover) no run has followed
    'latest check'?: IJob
}

// a Verify or Discover: a test of the pipeline's settings and a search for its streams, not a run (MEL-702)
export const isConnectionCheck = (job?: IJob) =>
    !!job && [JobType.PipelineVerify, JobType.PipelineDiscover].includes(job.type)

/*
 * The pipeline's latest run and latest connection check. A catalog that predates the split sends
 * only 'latest job', so fall back to it, by its type.
 */
export const latestRun = (pipeline: IPipeline): IJob | undefined => {
    const latestJob = pipeline._embedded?.['latest job']
    return pipeline._embedded?.['latest run'] ?? (isConnectionCheck(latestJob) ? undefined : latestJob)
}

export const latestCheck = (pipeline: IPipeline): IJob | undefined => {
    const latestJob = pipeline._embedded?.['latest job']
    return pipeline._embedded?.['latest check'] ?? (isConnectionCheck(latestJob) ? latestJob : undefined)
}

export default interface IPipeline extends INamedEntity, ITimestampedEntity {
    label: string
    status: PipelineStatus
    schedule?: string
    timeout: number
    maxRetries: number
    script?: string
    actions: string[]
    dataComponents: string[]
    properties: { [property: string]: string }
    triggeredBy: string[]
    /** Missing only on a pipeline that hasn't been created yet, or from an older API: read it as a workflow. */
    type?: PipelineType
    /**
     * What stops the pipeline being published and run as its type, each as "what is wrong: how to
     * fix it" — e.g. a draft ingestion its pipeline file added dbt to (MEL-702). Empty or missing
     * when there is nothing to fix; the list carries it for draft ingestions only.
     */
    problems?: string[]
    /** an ingestion that has run: its data store can't change (MEL-702) */
    dataStoreFixed?: boolean
    /** The pipeline's file in the workspace repository, for a pipeline deployed from one. */
    repositoryPath?: string
    /**
     * Carried by the workspace pipelines list and by `GET pipeline`, but not by the create/update
     * responses, so every read must tolerate `undefined` — see `DataRow.getPipeline`, which carries
     * the last known verdict forward rather than letting the chip vanish on a health-less payload.
     */
    health?: IPipelineHealth
    _embedded?: IPipelineEmbedded
}

export interface IPipelineEnvironment {
    [key: string]: {
        value: string
        sensitive: boolean
    }
}
